import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Connect, PropsFromRedux } from './SearchSectionContainer';
import { KeywordsCarouselWizard } from '../keywords-carousel/KeywordsCarouselWizard';
import { SearchAdviceWizard } from '../search-advice/SearchAdviceWizard';
import { SearchAutoCorrectWizard } from '../auto-correct/SearchAutoCorrectWizard';
import { ZeroResultsRouter } from '../zero-results/ZeroResultsRouter';
import { MainSearchSnippets } from '../search-snippets/main/MainSearchSnippets';
import { AsideSearchSnippets } from '../search-snippets/aside/AsideSearchSnippets';
import { SearchResults } from '../search-results/SearchResults';
import { TopLimitation } from '../top-limitation/TopLimitation';
import { MobileActions } from '../mobile-actions/MobileActions';
import { FilterBox } from '../search-snippets/aside/filter-box/FilterBox';
import { LoaderWithText } from '../../../components/loader/LoaderWithText';
import { SearchHeaderStatus } from './SearchHeaderStatus';
import { SEARCH_TYPES_WITH_FILTERS } from '../../searchContansts';
import { useUser } from '../../../user/useUser';
import styles from './search_section.module.scss';
import { SearchPaginationLoader } from './pagination-loader/SearchPaginationLoader';
import { SearchErrorMessage } from './error/SearchErrorMessage';
import { SearchEndResultsMessage } from './end-results/SearchEndResultsMessage';
import { SearchFiltersSort } from '../search-filters-bar/sort/SearchFiltersSort';
import {
  reportFilterToggle,
  filterActionTypes,
} from '../mobile-actions/filtersUtils';

type Props = {
  parentContainerRef?: HTMLDivElement | null;
} & PropsFromRedux;

const RawSearchSection: FC<Props> = ({
  enableWatchers,
  enableAdvice,
  enableSnippets,
  parentContainerRef,
  loaded,
  searchLoading,
  error,
  paginationLoading,
  totalHits,
  hasResults,
  endOfResults,
  type,
  searchTime,
  sortType,
  hits,
  ended,
  needMoreHits,
}) => {
  const { loggedIn } = useUser();
  const seeAllResultsMessage = hasResults && loaded && endOfResults && loggedIn;
  function track(actionType: filterActionTypes, value: boolean) {
    reportFilterToggle(type, actionType, value);
  }
  return (
    <>
      <div className={styles.container}>
        {enableWatchers && <MobileActions />}

        <div className={styles.desktopContainer}>
          <div className={styles.headerActions}>
            {!searchLoading && loaded && totalHits > -1 && (
              <SearchHeaderStatus
                searchTime={searchTime}
                totalHits={totalHits}
                type={type}
              />
            )}
            {searchLoading && (
              <LoaderWithText>
                <FormattedMessage id="search/SearchSection.tsx/1" />
              </LoaderWithText>
            )}
            {(type === 'arret' || type === 'commentary') && (
              <SearchFiltersSort
                sortType={sortType}
                track={track}
                searchType={type}
              />
            )}
          </div>

          {enableAdvice && <SearchAdviceWizard />}
          <SearchAutoCorrectWizard />
          <div>
            {enableSnippets && <MainSearchSnippets />}
            <div className={styles.asideContainer}>
              {SEARCH_TYPES_WITH_FILTERS.includes(type) && (
                <FilterBox
                  searchType={type as 'arret' | 'commentary' | 'law'}
                />
              )}
              {enableSnippets && <AsideSearchSnippets />}
            </div>

            {loaded && (
              <div className={styles.results}>
                <KeywordsCarouselWizard />
                <SearchResults
                  loggedIn={loggedIn}
                  hits={hits}
                  ended={ended}
                  type={type}
                  isSearchLoading={searchLoading}
                  isPaginationLoading={paginationLoading}
                  needMoreHits={needMoreHits}
                  parentContainerRef={parentContainerRef}
                />
                <TopLimitation />
              </div>
            )}

            {paginationLoading && <SearchPaginationLoader />}

            {seeAllResultsMessage && <SearchEndResultsMessage />}

            {error && <SearchErrorMessage error={error} />}

            <ZeroResultsRouter />
          </div>
        </div>
      </div>
    </>
  );
};

export const SearchSection = Connect(RawSearchSection);
